import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from 'antd';
import UserMenuDropdown from '@components/UserMenuDropdown';
import SearchBar from '@components/SearchBar';
import CampaignBreadcrumbs from '@components/CampaignBreadcrumbs';
import { ReactComponent as HelpSvg } from '@assets/icons/help.svg';
import { ReactComponent as UploadSvg } from '@assets/icons/upload.svg';
import { useDispatch } from 'react-redux';
import {
  toggleBootcampModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { NotificationFeed } from '@components/NotificationFeed';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useParams, useSearchParams } from 'react-router-dom';
import { nonNull } from '@helpers/non-null';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ReactComponent as BookIcon } from '@assets/icons/info/book.svg';
import { ReactComponent as BootcampIcon } from '@assets/icons/info/bootcamp.svg';
import { ReactComponent as HelpCircleIcon } from '@assets/icons/info/help-circle.svg';
import { ReactComponent as ChatIcon } from '@assets/icons/info/chat.svg';
import { ReactComponent as MessageSquareIcon } from '@assets/icons/info/message-square.svg';
import { ReactComponent as NewsFillIcon } from '@assets/icons/info/news-fill.svg';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import * as CookieConsent from 'vanilla-cookieconsent';
import { SettingOutlined } from '@ant-design/icons';

function AppHeader() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isShowTourPause = searchParams.get('startTour') === 'tour-paused';
  const isShowTourUploadAsset =
    searchParams.get('startTour') === 'upload-asset';
  const { onStartTour, tourData } = useOnboardingProcess();
  const { campaignId, folderId } = useParams();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
  const [currentWorkspace] = useCurrentWorkspace(false);

  useEffect(() => {
    if (!isShowTourPause) return;
    setIsOpenDropdown(true);
    const timeoutId = setTimeout(() => {
      onStartTour({
        type: 'tour-paused',
        onClose: () => {
          setIsOpenDropdown(false);
        }
      });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [isShowTourPause, onStartTour, setIsOpenDropdown]);

  useEffect(() => {
    if (!isShowTourUploadAsset) return;
    const timeoutId = setTimeout(() => {
      onStartTour({
        type: 'upload-asset'
      });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [isShowTourUploadAsset, onStartTour]);

  const menuItems: ItemType[] = useMemo(() => {
    return [
      {
        key: 'chatWithUs',
        label: 'Chat with us',
        icon: <ChatIcon />,
        onClick: () => {
          window.open(
            'https://www.streamwork.com/contact#hs-chat-open',
            '_blank'
          );
        }
      },
      {
        type: 'divider'
      },
      {
        key: 'bootcamp',
        label: 'Bootcamp',
        icon: <BootcampIcon />,
        onClick: () => {
          setIsOpenDropdown(false);
          dispatch(
            toggleBootcampModal({
              visible: true
            })
          );
        }
      },
      {
        key: 'helpCenter',
        label: 'Help center',
        icon: <BookIcon />,
        onClick: () => {
          window.open('https://help.streamwork.com/knowledge', '_blank');
        }
      },
      {
        key: 'contactSupport',
        label: 'Contact support',
        icon: <HelpCircleIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/contact', '_blank');
        }
      },
      {
        key: 'submitFeedback',
        label: 'Submit feedback',
        icon: <MessageSquareIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/contact', '_blank');
        }
      },
      {
        key: 'privacySettings',
        label: 'Privacy settings',
        icon: (
          <span
            style={{
              display: 'inline-block',
              width: 24,
              height: 24,
              textAlign: 'center',
              lineHeight: '20px'
            }}
          >
            <SettingOutlined style={{ fontSize: 20 }} />
          </span>
        ),
        onClick: () => {
          CookieConsent.reset(true);
          window.location.reload();
        }
      },
      {
        type: 'divider'
      },
      {
        key: 'whatNew',
        label: 'What’s new',
        icon: <NewsFillIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/blog', '_blank');
        }
      }
    ];
  }, [setIsOpenDropdown, dispatch]);

  const onOpenChangeDropdown = useCallback(
    (value: boolean) => {
      if (tourData?.type === 'tour-paused') return;
      setIsOpenDropdown(value);
    },
    [tourData?.type, setIsOpenDropdown]
  );

  return (
    <div className="header_container">
      <CampaignBreadcrumbs showSearch={showSearch} />
      <SearchBar showSearch={showSearch} setShowSearch={setShowSearch} />
      <Button
        type="primary"
        className="header_container__btn tour-upload-asset"
        disabled={!currentWorkspace}
        onClick={() =>
          dispatch(
            toggleUploadAssetModal({
              visible: true,
              postAction: 'redirect-to-campaign',
              workspaceId: nonNull(currentWorkspace).id,
              campaignId,
              folderId
            })
          )
        }
      >
        <UploadSvg />
        Upload
      </Button>
      <NotificationFeed />
      <Dropdown
        menu={{
          items: menuItems
        }}
        open={isOpenDropdown}
        onOpenChange={onOpenChangeDropdown}
        placement="bottomRight"
        overlayClassName="help_dropdown header_help_dropdown tour-paused"
        trigger={['click']}
      >
        <Button
          className="header_container__help tour-paused-btn"
          type="text"
          onClick={(e) => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
        >
          <HelpSvg />
        </Button>
      </Dropdown>
      <UserMenuDropdown />
    </div>
  );
}

export default AppHeader;
